/**
 * Resolve a barragem ativa do operador.
 *
 * POC: pega a primeira do array `barragem_ids` do engineer e busca o id da dam.
 * Futuro: vira um seletor explícito quando o app suportar barragens múltiplas
 *         na mesma sessão (Telles: barragens A/B/C compartilhadas).
 *
 * Retorna o uuid de `dams.id` correspondente ao primeiro code disponível,
 * ou undefined enquanto carrega.
 *
 * GUPM-167: a leitura de `dams` mudou de `supabase.from('dams')` direto para
 * o datasource `estrutura` (regra inviolável #2). Assinaturas públicas intactas.
 */

import { useQuery } from '@tanstack/react-query';
import { useDataSources } from '@/services/datasources';
import type { DamOption } from '@/services/datasources';
import { useCurrentEngineer } from './hooks';

// Re-export do tipo centralizado no datasource — preserva a superfície pública
// do módulo (o original definia e exportava DamOption daqui).
export type { DamOption };

export function useDamId(): string | undefined {
  const { estrutura } = useDataSources();
  const { data: engineer } = useCurrentEngineer();
  const firstCode = engineer?.barragemIds[0];

  const { data } = useQuery({
    queryKey: ['dam-id-by-code', firstCode],
    enabled: !!firstCode,
    staleTime: 30 * 60 * 1000,
    queryFn: () => estrutura.getDamIdByCode(firstCode!),
  });

  return data ?? undefined;
}

export function useDamCode(): string | undefined {
  const { data: engineer } = useCurrentEngineer();
  return engineer?.barragemIds[0];
}

export function useAvailableDams() {
  const { estrutura } = useDataSources();
  const { data: engineer } = useCurrentEngineer();
  const codes = engineer?.barragemIds ?? [];
  return useQuery({
    queryKey: ['available-dams', codes.join(',')],
    enabled: codes.length > 0,
    staleTime: 30 * 60 * 1000,
    queryFn: () => estrutura.listDamsByCodes(codes),
  });
}
